import { motion } from 'framer-motion';
import {
  BookOpen,
  Video,
  ClipboardList,
  BarChart3,
  Award,
  Smartphone,
  Users,
  FileText,
  Send,
  Bell,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function LearningManagementPage() {
  const features = [
    {
      icon: BookOpen,
      title: 'Course & Lesson Builder',
      description:
        'Create structured courses with modules, lessons, reading material, and downloadable resources for any subject or program.',
      color: 'bg-blue-500',
    },
    {
      icon: Video,
      title: 'Video & Live Class Support',
      description:
        'Upload recorded lectures or host live sessions. Students can learn anytime with on-demand video content.',
      color: 'bg-purple-500',
    },
    {
      icon: ClipboardList,
      title: 'Assignments & Quizzes',
      description:
        'Design assignments, online quizzes, and assessments with auto-grading. Set deadlines and track submissions easily.',
      color: 'bg-emerald-500',
    },
    {
      icon: BarChart3,
      title: 'Progress & Performance Tracking',
      description:
        'Monitor student engagement, completion rates, and scores. Identify learners who need extra support at a glance.',
      color: 'bg-pink-500',
    },
    {
      icon: Award,
      title: 'Certificates & Achievements',
      description:
        'Issue digital certificates on course completion. Motivate learners with badges and milestone achievements.',
      color: 'bg-yellow-500',
    },
    {
      icon: Smartphone,
      title: 'Mobile-Friendly Learning',
      description:
        'Students access courses on any device. Learn on the go with a responsive experience built for phones and tablets.',
      color: 'bg-cyan-600',
    },
    {
      icon: Users,
      title: 'Teacher & Faculty Dashboard',
      description:
        'Empower educators to manage classes, upload content, grade work, and communicate with students from one place.',
      color: 'bg-green-500',
    },
    {
      icon: FileText,
      title: 'Study Material Library',
      description:
        'Centralized repository for notes, PDFs, presentations, and reference documents organized by course and topic.',
      color: 'bg-indigo-500',
    },
    {
      icon: Send,
      title: 'Automated Notifications',
      description:
        'Send reminders for assignments, exam dates, and new content via WhatsApp and in-app alerts.',
      color: 'bg-green-600',
    },
    {
      icon: Bell,
      title: 'Announcements & Discussions',
      description:
        'Broadcast updates to enrolled students and enable discussion threads for collaborative learning.',
      color: 'bg-orange-500',
    },
  ];

  const deliverables = [
    'Course creation with modules, lessons, and multimedia content',
    'Online assignments, quizzes, and automated grading',
    'Student and teacher portals with role-based access',
    'Progress dashboards and performance reports',
    'Integration with your existing school or college management system',
  ];

  return (
    <div data-testid="learning-management-page" className="pt-20">
      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 mb-6">
              <BookOpen className="h-4 w-4 text-emerald-light" />
              <span className="text-sm font-medium text-slate-100">
                Learning Management Solution
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black mb-6 font-heading">
              Smart Learning For Schools & Colleges
            </h1>
            <p className="text-base leading-relaxed text-slate-300 max-w-3xl mx-auto mb-8">
              Deliver engaging digital education with a complete LMS. Create courses,
              conduct assessments, track progress, and empower teachers and students
              to learn beyond the classroom.
            </p>
            <Link to="/register">
              <Button
                data-testid="lms-demo-button"
                size="lg"
                className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
              >
                Book Free Demo
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section
        data-testid="lms-features-grid-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Everything You Need To Teach Online
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A full-featured learning platform designed for Indian schools and colleges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`lms-feature-module-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div
                  className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
              Core Deliverables
            </h2>
            <div className="space-y-4">
              {deliverables.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="lms-cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            Ready To Transform How You Teach?
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Book a personalized demo to see how our Learning Management Solution
            can power digital education at your institution.
          </p>
          <Link to="/register">
            <Button
              data-testid="lms-cta-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
            >
              Book Free Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
